/* eslint-disable no-shadow */
/* eslint-disable no-extra-boolean-cast */
/* eslint-disable no-plusplus */
import {
	InterestPointStatus,
	InterestPointType,
	RepossessionsLocationTypes,
} from 'models/repossession/enums/repossession.enums';
import { commonErrorHandler } from 'models/shared/helpers/errors';
import moment from 'moment';
import React, { useEffect, useState, createContext, useMemo } from 'react';
import {
	FetchLocations,
	FetchRecoveriesByLoan,
} from 'models/repossession/controllers/repossession.controller';
import { RepossessionLocation } from 'models/repossession/interfaces/repossession.interface';
import { Recovery } from 'models/repossession/interfaces/recovery.interface';
import { findAllOzonios } from 'models/ozonio/controllers/ozonios.controller';
import { fetchTilesLocations } from 'models/geozon/controllers/tile.controller';
import { TileTrackerLocations } from 'models/geozon/interfaces/tile.interface';
import { PositionServiceClient } from 'proto/service_grpc_web_pb';
import protoDefinition from 'proto/service_pb';
import { ClientReadableStream } from 'grpc-web';
import { InterestPoint } from 'models/repossession/interfaces/interestPoint.interface';
import {
	getRepossessionRoute,
	setRepossessionRoute,
} from 'localStorage/repossessionStorage';
import { RecoveryStatus } from 'models/repossession/enums/recovery.enums';
import { getUser } from 'localStorage/authStorage';
import {
	PlainLocation,
	RepossessionTabs,
	CreateRouteSteps,
	TableInfo,
} from './Repossessions.constants';

interface RepossessionContextModel {
	loading: boolean;
	setLoading: React.Dispatch<React.SetStateAction<boolean>>;
	localStorageLoaded: boolean;
	viewTab: RepossessionTabs;
	setViewTab: React.Dispatch<React.SetStateAction<RepossessionTabs>>;
	createRouteStep: CreateRouteSteps | null;
	setCreateRouteStep: React.Dispatch<
		React.SetStateAction<CreateRouteSteps | null>
	>;
	ozonios: { value: string; label: string }[];
	repossessionsData: {
		[key: string]: RepossessionLocation & { tileUuid?: string };
	};
	recoveriesData: { [key: string]: Recovery[] };
	setRecoveriesData: React.Dispatch<{ [key: string]: Recovery[] }>;
	initialPoints: PlainLocation[];
	noPointRepossessions: string[];
	interestPoints: { [key: string]: PlainLocation[] };
	gpsLocations: { [key: string]: PlainLocation | null };
	tileLocations: { [key: string]: PlainLocation | null };
	selectedMarkers: Set<string>;
	setSelectedMarkers: React.Dispatch<React.SetStateAction<Set<string>>>;
	selectedRepossessions: TableInfo[];
	setSelectedRepossessions: React.Dispatch<React.SetStateAction<TableInfo[]>>;
	selectedInterestPoints: { [key: string]: PlainLocation[] };
	setSelectedInterestPoints: React.Dispatch<{ [key: string]: PlainLocation[] }>;
	clearContext: () => void;
}

export const repossessionContext = createContext<RepossessionContextModel>({
	loading: false,
} as any);

export const RepossessionContextProvider = ({
	children,
}: {
	children: any;
}) => {
	const [loading, setLoading] = useState<boolean>(false);
	const [viewTab, setViewTab] = useState(RepossessionTabs.repossession);
	const [createRouteStep, setCreateRouteStep] =
		useState<CreateRouteSteps | null>(null);
	const [ozonios, setOzonios] = useState<{ value: string; label: string }[]>(
		[],
	);

	const [repossessionsData, setRepossessionsData] = useState<{
		[key: string]: RepossessionLocation & { tileUuid?: string };
	}>({});

	const [recoveriesData, setRecoveriesData] = useState<{
		[key: string]: Recovery[];
	}>({});

	// STEP 1
	const [gpsStream, setGpsStream] =
		useState<ClientReadableStream<proto.position.PositionResponse>>();

	const [noPointRepossessions, setNoPointRepossessions] = useState<string[]>(
		[],
	);
	const [imeiMap, setImeiMap] = useState<{
		[key: string]: string;
	}>({});
	const [gpsLocations, setGpsLocations] = useState<{
		[key: string]: PlainLocation | null;
	}>({});
	const [tileLocations, setTileLocations] = useState<{
		[key: string]: PlainLocation | null;
	}>({});
	const [interestPoints, setInterestPoints] = useState<{
		[key: string]: PlainLocation[];
	}>({});
	const [initialPoints, setInitialPoints] = useState<PlainLocation[]>([]);

	const [selectedMarkers, setSelectedMarkers] = useState<Set<string>>(
		new Set(),
	);

	// STEP 2
	const [selectedRepossessions, setSelectedRepossessions] = useState<
		TableInfo[]
	>([]);

	// STEP 3
	const [selectedInterestPoints, setSelectedInterestPoints] = useState<{
		[key: string]: PlainLocation[];
	}>({});

	const [localStorageLoaded, setLocalStorageLoaded] = useState(false);

	const clearContext = () => {
		setCreateRouteStep(null);
		setRepossessionsData({});
		setRecoveriesData({});
		setNoPointRepossessions([]);
		setGpsLocations({});
		setTileLocations({});
		setInterestPoints({});
		setInitialPoints([]);
		setImeiMap({});
		setSelectedMarkers(new Set());
		setSelectedRepossessions([]);
		setSelectedInterestPoints({});
		initializeContext();
	};

	const initializeContext = () => {
		setLoading(true);
		Promise.all([fetchTilesLocations(), FetchLocations()])
			.then(([tiles, gps]) => {
				setLoading(false);
				const locations: {
					[key: string]: PlainLocation | null;
				} = {};
				const tLocations: {
					[key: string]: PlainLocation | null;
				} = {};
				const interestPoint: {
					[key: string]: PlainLocation[];
				} = {};
				const data: {
					[key: string]: RepossessionLocation & { tileUuid?: string };
				} = {};
				const imeis: {
					[key: string]: string;
				} = {};
				const tilesData: {
					[key: string]: TileTrackerLocations;
				} = {};
				tiles.forEach((tile) => {
					tilesData[tile.vehicle.internalId] = tile;
				});
				gps.forEach((location) => {
					const tilePoint = tilesData[location.internalId];
					data[location.loanId] = { ...location, tileUuid: tilePoint?._id };
					interestPoint[location.loanId] = [];
					locations[location.loanId] = null;
					if (location.imei) {
						imeis[location.imei] = location.loanId;
					}

					tLocations[location.loanId] = tilePoint
						? {
								loanId: location.loanId,
								repossessionId: location.repossessionId,
								vehicleId: location.internalId,
								locationType:
									Math.abs(
										moment(tilePoint.location.updatedAt).diff(
											moment(),
											'hours',
										),
									) < 24
										? InterestPointType.signalTileVehicle
										: InterestPointType.noSignalTileVehicle,
								coordinates: {
									lat: tilePoint.location.latitude,
									long: tilePoint.location.longitude,
								},
								createdAt: tilePoint.updatedAt,
								id: `${location.loanId}_${InterestPointType.signalTileVehicle}`,
						  }
						: null;

					location.locations.forEach((loc) => {
						if (loc.type === RepossessionsLocationTypes.gps) {
							locations[location.loanId] = {
								loanId: location.loanId,
								repossessionId: location.repossessionId,
								vehicleId: location.internalId,
								locationType:
									Math.abs(moment(loc.updatedAt).diff(moment(), 'hours')) < 24
										? InterestPointType.signalVehicle
										: InterestPointType.noSignalVehicle,
								coordinates: loc.coordinates,
								createdAt: loc.updatedAt,
								id: `${location.loanId}_${InterestPointType.signalVehicle}`,
							};
						} else {
							interestPoint[location.loanId].push({
								loanId: location.loanId,
								repossessionId: location.repossessionId,
								externalId: loc.id,
								vehicleId: location.internalId,
								locationType: loc.type as any,
								coordinates: loc.coordinates,
								createdAt: loc.updatedAt,
								id: `${location.loanId}_${loc.id ?? loc.type}`,
							});
						}
					});
				});
				setRepossessionsData(data);
				setGpsLocations(locations);
				setTileLocations(tLocations);
				setInterestPoints(interestPoint);
				setImeiMap(imeis);
				loadLocalStorage();
			})
			.catch((e) => {
				setLoading(false);
				commonErrorHandler(e);
			});
	};

	useEffect(() => {
		if (localStorageLoaded)
			setRepossessionRoute({
				createRouteStep,
				selectedMarkers: Array.from(selectedMarkers),
				selectedRepossessions,
				selectedInterestPoints,
			});
	}, [
		createRouteStep,
		selectedMarkers,
		selectedRepossessions,
		selectedInterestPoints,
	]);

	useEffect(() => {
		if (gpsStream) {
			gpsStream.cancel();
			setGpsStream(undefined);
		}
		if (!Object.keys(imeiMap)?.length) return;
		const client = new PositionServiceClient(
			process.env.REACT_APP_GRPC_PROXY_URL ?? 'http://localhost:8080',
			null,
			null,
		);

		const request = new (protoDefinition as any).PositionRequest();
		Object.keys(imeiMap).forEach((imei) => {
			request.addImeis(imei, 0);
		});
		const locationsStream = client.streamPosition(request, null);
		locationsStream.on('data', (response: proto.position.PositionResponse) => {
			const imei = response.getImei();
			const timestamp = response.getTimestamp();
			const coordinates = {
				lat: response.getLatitude(),
				long: response.getLongitude(),
			};
			const loanId = imeiMap[imei];
			const location = repossessionsData[loanId];
			setGpsLocations((prev) => {
				const locations = { ...prev };
				// eslint-disable-next-line no-param-reassign
				locations[loanId] = {
					loanId: location.loanId,
					repossessionId: location.repossessionId,
					vehicleId: location.internalId,
					locationType: InterestPointType.signalVehicle,
					coordinates,
					createdAt: timestamp,
					id: `${location.loanId}_${InterestPointType.signalVehicle}`,
				};
				return locations;
			});
		});
		setGpsStream(locationsStream);
	}, [imeiMap]);

	useEffect(() => {
		initializeContext();
		const user = getUser();
		findAllOzonios(user?.company._id ?? '', '', true).then((ozoniosListing) => {
			setOzonios(
				ozoniosListing.data
					.sort((a, b) => {
						if (a.name < b.name) {
							return -1;
						}
						if (a.name > b.name) {
							return 1;
						}
						return 0;
					})
					.map((ozonio) => {
						return {
							value: ozonio._id,
							label: ozonio.name,
						};
					}),
			);
		});
		return () => {
			gpsStream?.cancel();
		};
	}, []);

	useEffect(() => {
		const initial: PlainLocation[] = [];
		const noPoint: string[] = [];
		Object.keys(gpsLocations).forEach((key) => {
			const gps = gpsLocations[key];
			const gpsTime = gps?.createdAt ? new Date(gps.createdAt).getTime() : 0;
			const tile = tileLocations[key];
			const tileTime = tile?.createdAt ? new Date(tile.createdAt).getTime() : 0;
			const preferredPoint = gpsTime > tileTime ? gps : tile;
			const address = interestPoints[key]?.find((point) => {
				return point.locationType === InterestPointType.address;
			});
			const accountAccess = interestPoints[key]?.find((point) => {
				return point.locationType === InterestPointType.geolocation;
			});

			if (
				preferredPoint?.locationType === InterestPointType.signalVehicle ||
				preferredPoint?.locationType === InterestPointType.signalTileVehicle
			) {
				initial.push(preferredPoint);
				return;
			}
			if (address && address.coordinates.lat !== 0) {
				initial.push(address);
				return;
			}
			if (accountAccess) {
				initial.push(accountAccess);
				return;
			}
			if (preferredPoint) {
				initial.push(preferredPoint);
				return;
			}
			noPoint.push(repossessionsData[key]?.internalId);
		});
		setInitialPoints(initial);
		setNoPointRepossessions(noPoint);
	}, [gpsLocations, interestPoints, repossessionsData, tileLocations]);

	useEffect(() => {
		if (selectedMarkers?.size && (createRouteStep ?? 0) >= 2) {
			const allRecoveries: {
				[key: string]: Recovery[];
			} = {};
			Promise.all(
				Array.from(selectedMarkers).map((marker) =>
					FetchRecoveriesByLoan(marker).then((recoveries) => {
						allRecoveries[marker] = recoveries.filter((item) =>
							[RecoveryStatus.recovered, RecoveryStatus.failed].includes(
								item.status,
							),
						);
					}),
				),
			).then(() => {
				setRecoveriesData(allRecoveries);
			});
		}
	}, [selectedMarkers, createRouteStep]);

	useEffect(() => {
		if (interestPoints && recoveriesData) {
			const filledInterestPoints: { [key: string]: PlainLocation[] } = {
				...interestPoints,
			};
			Object.entries(recoveriesData).forEach(([key, recoveries]) => {
				const locations = [...(filledInterestPoints[key] ?? [])];
				const interestPoints = new Set<InterestPoint>();
				recoveries
					.filter(
						(recovery) =>
							![
								InterestPointType.noSignalTileVehicle,
								InterestPointType.noSignalVehicle,
								InterestPointType.signalTileVehicle,
								InterestPointType.signalVehicle,
							].includes(recovery.interestPoint.type),
					)
					.forEach((recovery) => {
						interestPoints.add(recovery.interestPoint);
					});
				Array.from(interestPoints).forEach((interestPoint) => {
					const id = `${key}_${interestPoint.externalId ?? interestPoint.type}`;
					const index = locations.findIndex((loc) => loc.id === id);
					if (index !== -1) {
						locations[index].interestPointId = interestPoint._id;
					} else {
						locations.push({
							loanId: key,
							repossessionId: repossessionsData[key].repossessionId,
							externalId: interestPoint.externalId,
							vehicleId: repossessionsData[key].internalId,
							locationType: interestPoint.type,
							coordinates: interestPoint.coordinates,
							createdAt: interestPoint.eventDate,
							id,
							interestPointId: interestPoint._id,
						});
					}
				});
				filledInterestPoints[key] = locations;
			});
			setInterestPoints(filledInterestPoints);
		}
	}, [recoveriesData]);

	const loadLocalStorage = () => {
		const reposData = getRepossessionRoute();
		setLocalStorageLoaded(true);
		if (reposData && Object.keys(reposData).length) {
			setCreateRouteStep(reposData.createRouteStep);
			setSelectedRepossessions(reposData.selectedRepossessions);
			setSelectedMarkers(new Set(reposData.selectedMarkers));
			setSelectedInterestPoints(reposData.selectedInterestPoints);
		}
	};

	const contextValue: RepossessionContextModel = useMemo(
		() => ({
			loading,
			setLoading,
			localStorageLoaded,
			viewTab,
			setViewTab,
			createRouteStep,
			setCreateRouteStep,
			ozonios,
			repossessionsData,
			recoveriesData,
			setRecoveriesData,
			initialPoints,
			noPointRepossessions,
			interestPoints,
			gpsLocations,
			tileLocations,
			selectedMarkers,
			setSelectedMarkers,
			selectedRepossessions,
			setSelectedRepossessions,
			selectedInterestPoints,
			setSelectedInterestPoints,
			clearContext,
		}),
		[
			loading,
			localStorageLoaded,
			viewTab,
			createRouteStep,
			ozonios,
			repossessionsData,
			recoveriesData,
			initialPoints,
			noPointRepossessions,
			interestPoints,
			gpsLocations,
			tileLocations,
			selectedMarkers,
			selectedRepossessions,
			selectedInterestPoints,
		],
	);
	return (
		<repossessionContext.Provider value={contextValue}>
			{children}
		</repossessionContext.Provider>
	);
};
